import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface OrderShipment {
  id: string;
  order_id: string;
  shipment_number: number;
  status: 'pending' | 'ready_to_ship' | 'shipped' | 'delivered' | 'confirmed';
  allocated_device_ids: string[];
  allocated_credits: number;
  tracking_number?: string;
  carrier?: string;
  delivery_address_id?: string;
  notes?: string;
  shipped_at?: string;
  delivered_at?: string;
  confirmed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface DealCreditConfig {
  coverageYears: number;
  creditsPerCamera: number;
  annualCreditsPerCamera: number;
  dealTitle: string;
}

export const useOrderShipments = (orderId?: string) => {
  return useQuery({
    queryKey: ['order-shipments', orderId],
    queryFn: async () => {
      if (!orderId) return [];
      
      const { data, error } = await supabase
        .from('order_shipments')
        .select('*')
        .eq('order_id', orderId)
        .order('shipment_number', { ascending: true });

      if (error) {
        console.error("Error fetching order shipments:", error);
        throw error;
      }

      return data as OrderShipment[];
    },
    enabled: !!orderId,
  });
};

// Helper to fetch deal credit configuration for an order
export const useDealCreditConfig = (orderId?: string) => {
  return useQuery({
    queryKey: ['deal-credit-config', orderId],
    queryFn: async (): Promise<DealCreditConfig | null> => {
      if (!orderId) return null;

      // Get order -> sale -> deal to retrieve credit configuration
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          id,
          sales!inner(
            crm_deals!inner(
              id,
              title,
              deal_data
            )
          )
        `)
        .eq('id', orderId)
        .maybeSingle();

      if (orderError) {
        console.error("Error fetching deal data:", orderError);
        return null;
      }

      if (!orderData?.sales?.crm_deals) {
        console.warn("No deal data found for order:", orderId);
        return null;
      }

      const deal = orderData.sales.crm_deals;
      const dealData = deal.deal_data as Record<string, any> | null;

      if (!dealData) {
        return null;
      }

      const coverageYears = dealData.coverage_years || 1;
      const creditsPerCamera = dealData.credits_per_camera || dealData.total_images || 0;
      const annualCreditsPerCamera = creditsPerCamera;

      return {
        coverageYears,
        creditsPerCamera,
        annualCreditsPerCamera,
        dealTitle: deal.title || 'Order'
      };
    },
    enabled: !!orderId,
  });
};

export const useCreateShipment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      orderId,
      allocatedDeviceIds,
      allocatedCredits,
      deliveryAddressId,
      notes
    }: {
      orderId: string;
      allocatedDeviceIds: string[];
      allocatedCredits: number;
      deliveryAddressId?: string;
      notes?: string;
    }) => {
      console.log("🚀 Starting enhanced shipment creation for order:", orderId);
      console.log("📦 Devices:", allocatedDeviceIds);
      console.log("💳 Credits:", allocatedCredits);

      // Step 1: Fetch full order data with deal configuration
      console.log("📝 Step 1: Fetching order and deal data...");
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          id,
          customer_user_id,
          organization_id,
          allocated_device_ids,
          sales!inner(
            crm_deals!inner(
              id,
              title,
              deal_data
            )
          )
        `)
        .eq('id', orderId)
        .single();

      if (orderError) {
        console.error("❌ Failed to fetch order:", orderError);
        throw new Error(`Failed to fetch order: ${orderError.message}`);
      }

      if (!orderData) {
        throw new Error("Order not found");
      }

      const deal = orderData.sales?.crm_deals;
      const dealData = deal?.deal_data as Record<string, any> | null;
      const coverageYears = dealData?.coverage_years || 1;
      const creditsPerCamera = dealData?.credits_per_camera || dealData?.total_images || 0;
      const annualCreditsPerCamera = creditsPerCamera;
      const dealTitle = deal?.title || 'Order';

      console.log("✅ Order data fetched. Organization:", orderData.organization_id);
      console.log("📊 Deal config - Years:", coverageYears, "Annual credits/camera:", annualCreditsPerCamera);

      // Step 2: Get all admin users in the organization
      console.log("📝 Step 2: Finding admin users in organization...");
      const adminUserIds = orderData.customer_user_id ? [orderData.customer_user_id] : [];

      if (orderData.organization_id) {
        const { data: adminUsers, error: adminError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('organization_id', orderData.organization_id)
          .eq('role', 'admin');

        if (adminError) {
          console.warn("⚠️ Failed to fetch admin users:", adminError);
        } else if (adminUsers && adminUsers.length > 0) {
          const additionalAdminIds = adminUsers
            .map(u => u.user_id)
            .filter(id => id && !adminUserIds.includes(id));
          
          adminUserIds.push(...additionalAdminIds);
        }
      }
      
      console.log("👥 Admin user IDs for device assignments:", adminUserIds);

      // Step 3: Get the next shipment number and create shipment
      console.log("📝 Step 3: Creating shipment record...");
      const { data: existingShipments, error: countError } = await supabase
        .from('order_shipments')
        .select('shipment_number')
        .eq('order_id', orderId)
        .order('shipment_number', { ascending: false })
        .limit(1);

      if (countError) throw countError;

      const nextShipmentNumber = existingShipments && existingShipments.length > 0 
        ? existingShipments[0].shipment_number + 1 
        : 1;

      const { data: shipment, error: shipmentError } = await supabase
        .from('order_shipments')
        .insert({
          order_id: orderId,
          shipment_number: nextShipmentNumber,
          allocated_device_ids: allocatedDeviceIds,
          allocated_credits: allocatedCredits,
          delivery_address_id: deliveryAddressId,
          notes,
          status: 'pending'
        })
        .select()
        .single();

      if (shipmentError) throw shipmentError;
      console.log("✅ Shipment #", nextShipmentNumber, "created");

      // Step 4: Update order.allocated_device_ids
      if (allocatedDeviceIds.length > 0) {
        console.log("📝 Step 4: Updating order allocated devices...");
        const existingIds = orderData.allocated_device_ids || [];
        const newIds = allocatedDeviceIds.filter(id => !existingIds.includes(id));

        if (newIds.length > 0) {
          const { error: updateError } = await supabase
            .from('orders')
            .update({
              allocated_device_ids: [...existingIds, ...newIds],
              updated_at: new Date().toISOString()
            })
            .eq('id', orderId);

          if (updateError) {
            console.error("⚠️ Error updating order allocated_device_ids:", updateError);
          } else {
            console.log("✅ Order updated with", newIds.length, "new device IDs");
          }
        }
      }

      // Step 5: Create device assignments for ALL admin users
      if (allocatedDeviceIds.length > 0 && adminUserIds.length > 0) {
        console.log("📝 Step 5: Creating device assignments for admins...");
        
        // Check existing assignments
        const { data: existingAssignments, error: checkError } = await supabase
          .from('device_assignments')
          .select('device_id, user_id')
          .in('device_id', allocatedDeviceIds)
          .in('user_id', adminUserIds);

        if (checkError) {
          console.warn("⚠️ Failed to check existing assignments:", checkError);
        }

        const newAssignments = [];
        for (const userId of adminUserIds) {
          for (const deviceId of allocatedDeviceIds) {
            const exists = existingAssignments?.some(
              a => a.device_id === deviceId && a.user_id === userId
            );
            
            if (!exists) {
              newAssignments.push({
                device_id: deviceId,
                user_id: userId,
                assigned_at: new Date().toISOString()
              });
            }
          }
        }

        if (newAssignments.length > 0) {
          const { error: assignmentError } = await supabase
            .from('device_assignments')
            .insert(newAssignments);

          if (assignmentError) {
            console.error("⚠️ Failed to create device assignments:", assignmentError);
          } else {
            console.log("✅ Created", newAssignments.length, "device assignments for", adminUserIds.length, "users");
          }
        }
      }

      // Step 6: Create credit packages for each device (multi-year support)
      if (allocatedDeviceIds.length > 0 && annualCreditsPerCamera > 0) {
        console.log("📝 Step 6: Creating credit packages...");
        const currentYear = new Date().getFullYear();
        let packagesCreated = 0;
        let devicesUpdated = 0;

        for (const deviceId of allocatedDeviceIds) {
          // Check for existing packages to prevent duplicates
          const { data: existingPackages } = await supabase
            .from('device_credit_packages')
            .select('id')
            .eq('order_id', orderId)
            .eq('device_id', deviceId);

          if (existingPackages && existingPackages.length > 0) {
            console.log(`ℹ️ Packages already exist for device ${deviceId}, skipping`);
            continue;
          }

          // Create packages for each year
          for (let year = 1; year <= coverageYears; year++) {
            const isReleased = year === 1;
            const releaseDate = year === 1 
              ? null 
              : new Date(currentYear + (year - 1), 0, 1).toISOString();

            const { error: packageError } = await supabase
              .from('device_credit_packages')
              .insert({
                device_id: deviceId,
                order_id: orderId,
                organization_id: orderData.organization_id,
                package_name: `Year ${year} - ${dealTitle}`,
                total_credits: annualCreditsPerCamera,
                remaining_credits: annualCreditsPerCamera,
                assigned_credits: 0,
                year_number: year,
                is_released: isReleased,
                release_date: releaseDate,
                coverage_year: currentYear + (year - 1),
                assignment_type: 'customer_owned',
                purchaser_user_id: orderData.customer_user_id
              });

            if (packageError) {
              console.error(`⚠️ Failed to create package for device ${deviceId} year ${year}:`, packageError);
            } else {
              packagesCreated++;
            }
          }

          // Update device_details.images_allowed for Year 1 only
          const { data: currentDevice } = await supabase
            .from('device_details')
            .select('images_allowed')
            .eq('device_id', deviceId)
            .single();

          const currentAllowed = currentDevice?.images_allowed || 0;
          
          const { error: deviceUpdateError } = await supabase
            .from('device_details')
            .update({ 
              images_allowed: currentAllowed + annualCreditsPerCamera 
            })
            .eq('device_id', deviceId);

          if (!deviceUpdateError) {
            devicesUpdated++;
          } else {
            console.warn(`⚠️ Failed to update images_allowed for ${deviceId}:`, deviceUpdateError);
          }
        }

        console.log(`✅ Created ${packagesCreated} credit packages, updated ${devicesUpdated} devices`);
      }

      // Step 7: Call BuildShip API
      console.log("📝 Step 7: Calling BuildShip API...");
      try {
        const buildshipAuth = await supabase.functions.invoke('get-buildship-token');
        
        if (buildshipAuth.data?.token) {
          for (const deviceId of allocatedDeviceIds) {
            await fetch('https://i56o7w.buildship.run/device-assignment', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${buildshipAuth.data.token}`,
              },
              body: JSON.stringify({
                orderId: orderId,
                deviceId: deviceId,
                allocatedCredits: annualCreditsPerCamera,
                customerUserId: orderData.customer_user_id,
                organizationId: orderData.organization_id,
                assignedUserCount: adminUserIds.length,
                coverageYears: coverageYears,
                yearNumber: 1,
                totalCredits: creditsPerCamera
              }),
            });
          }
          console.log("✅ BuildShip API calls completed");
        } else {
          console.warn("⚠️ No BuildShip token available");
        }
      } catch (error) {
        console.warn("⚠️ BuildShip API call failed:", error);
        // Don't fail the entire operation if BuildShip API fails
      }

      console.log("🎉 Enhanced shipment creation completed successfully");
      return shipment;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['order-shipments', data.order_id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      queryClient.invalidateQueries({ queryKey: ['enhanced-credit-packages'] });
      queryClient.invalidateQueries({ queryKey: ['device-details'] });
      queryClient.invalidateQueries({ queryKey: ['device-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['reserved-credit-packages'] });
      toast({
        title: "Success",
        description: `Shipment #${data.shipment_number} created with credit packages and device assignments`,
      });
    },
    onError: (error) => {
      console.error("💥 Error creating shipment:", error);
      toast({
        title: "Error",
        description: "Failed to create shipment. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateShipment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      updates
    }: {
      shipmentId: string;
      updates: Partial<Omit<OrderShipment, 'id' | 'order_id' | 'shipment_number' | 'created_at' | 'updated_at'>>;
    }) => {
      const { data, error } = await supabase
        .from('order_shipments')
        .update(updates)
        .eq('id', shipmentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['order-shipments', data.order_id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      toast({
        title: "Success",
        description: "Shipment updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating shipment:", error);
      toast({
        title: "Error",
        description: "Failed to update shipment. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useAdvanceShipmentStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      action,
      metadata
    }: {
      shipmentId: string;
      action: 'ready_to_ship' | 'ship' | 'deliver' | 'confirm';
      metadata?: { trackingNumber?: string; carrier?: string };
    }) => {
      let updates: Partial<OrderShipment> = {};

      switch (action) {
        case 'ready_to_ship':
          updates.status = 'ready_to_ship';
          break;
        case 'ship':
          updates.status = 'shipped';
          updates.shipped_at = new Date().toISOString();
          if (metadata?.trackingNumber) {
            updates.tracking_number = metadata.trackingNumber;
          }
          if (metadata?.carrier) {
            updates.carrier = metadata.carrier;
          }
          break;
        case 'deliver':
          updates.status = 'delivered';
          updates.delivered_at = new Date().toISOString();
          break;
        case 'confirm':
          updates.status = 'confirmed';
          updates.confirmed_at = new Date().toISOString();
          break;
      }

      const { data, error } = await supabase
        .from('order_shipments')
        .update(updates)
        .eq('id', shipmentId)
        .select()
        .single();

      if (error) throw error;

      // After updating shipment status, sync the parent order status
      const orderId = data.order_id;
      const updatedShipmentId = data.id;
      const updatedShipmentStatus = data.status;

      // Fetch OTHER shipments for this order (exclude the one we just updated to avoid race condition)
      const { data: otherShipments } = await supabase
        .from('order_shipments')
        .select('id, status')
        .eq('order_id', orderId)
        .neq('id', updatedShipmentId);

      // Combine: the updated shipment (definitive) + other shipments
      const allShipmentStatuses = [
        updatedShipmentStatus,
        ...(otherShipments?.map(s => s.status) || [])
      ];

      if (allShipmentStatuses.length > 0) {
        const allConfirmed = allShipmentStatuses.every(s => s === 'confirmed');
        const allDelivered = allShipmentStatuses.every(s => 
          s === 'delivered' || s === 'confirmed'
        );
        const someShipped = allShipmentStatuses.some(s => 
          s === 'shipped' || s === 'delivered' || s === 'confirmed'
        );

        // Build order update based on shipment states
        const orderUpdate: Record<string, any> = { updated_at: new Date().toISOString() };

        if (allConfirmed) {
          // All shipments confirmed = order is fully complete
          orderUpdate.status = 'delivered';
          orderUpdate.fulfillment_status = 'completed';
          orderUpdate.fulfillment_completed_at = new Date().toISOString();
          console.log(`[Shipment Sync] All shipments confirmed - completing order ${orderId}`);
          
          // Also update the linked sale to 'completed'
          const { data: orderForSale } = await supabase
            .from('orders')
            .select('sale_id')
            .eq('id', orderId)
            .single();
            
          if (orderForSale?.sale_id) {
            const { error: saleError } = await supabase
              .from('sales')
              .update({
                status: 'completed',
                updated_at: new Date().toISOString()
              })
              .eq('id', orderForSale.sale_id);
              
            if (saleError) {
              console.error(`[Shipment Sync] Failed to update sale status:`, saleError);
            } else {
              console.log(`[Shipment Sync] Updated sale ${orderForSale.sale_id} to completed`);
            }
          }
        } else if (allDelivered) {
          orderUpdate.status = 'delivered';
          console.log(`[Shipment Sync] All shipments delivered - updating order ${orderId} to delivered`);
        } else if (someShipped) {
          orderUpdate.status = 'shipped';
          console.log(`[Shipment Sync] Some shipments shipped - updating order ${orderId} to shipped`);
        }

        if (Object.keys(orderUpdate).length > 1) {
          const { error: orderError } = await supabase
            .from('orders')
            .update(orderUpdate)
            .eq('id', orderId);
          
          if (orderError) {
            console.error(`[Shipment Sync] Failed to update order ${orderId}:`, orderError);
          }
        }
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['order-shipments', data.order_id] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      queryClient.invalidateQueries({ queryKey: ['order-workflow', data.order_id] });
      
      const statusMessages: Record<string, string> = {
        'ready_to_ship': 'Shipment is ready for shipping',
        'shipped': 'Shipment has been marked as shipped',
        'delivered': 'Shipment has been marked as delivered',
        'confirmed': 'Shipment has been confirmed by customer'
      };
      
      toast({
        title: "Success",
        description: statusMessages[data.status] || "Shipment status updated",
      });
    },
    onError: (error) => {
      console.error("Error advancing shipment status:", error);
      toast({
        title: "Error",
        description: "Failed to update shipment status. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteShipment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (shipmentId: string) => {
      // Get the order_id before deleting
      const { data: shipment, error: fetchError } = await supabase
        .from('order_shipments')
        .select('order_id')
        .eq('id', shipmentId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('order_shipments')
        .delete()
        .eq('id', shipmentId);

      if (error) throw error;
      return { orderId: shipment.order_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['order-shipments', data.orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order-management'] });
      toast({
        title: "Success",
        description: "Shipment deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting shipment:", error);
      toast({
        title: "Error",
        description: "Failed to delete shipment. Please try again.",
        variant: "destructive",
      });
    },
  });
};

// Helper to calculate shipment summary for an order
export const useShipmentSummary = (orderId?: string) => {
  const { data: shipments, isLoading } = useOrderShipments(orderId);
  
  if (isLoading || !shipments) {
    return {
      isLoading,
      totalShipments: 0,
      pendingCount: 0,
      shippedCount: 0,
      deliveredCount: 0,
      confirmedCount: 0,
      allDelivered: false,
      hasShipments: false
    };
  }

  const pendingCount = shipments.filter(s => s.status === 'pending' || s.status === 'ready_to_ship').length;
  const shippedCount = shipments.filter(s => s.status === 'shipped').length;
  const deliveredCount = shipments.filter(s => s.status === 'delivered').length;
  const confirmedCount = shipments.filter(s => s.status === 'confirmed').length;

  return {
    isLoading: false,
    totalShipments: shipments.length,
    pendingCount,
    shippedCount,
    deliveredCount,
    confirmedCount,
    allDelivered: shipments.length > 0 && shipments.every(s => s.status === 'delivered' || s.status === 'confirmed'),
    hasShipments: shipments.length > 0
  };
};
